import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Stack, CircularProgress, Alert, Tooltip,
  ToggleButton, ToggleButtonGroup, Chip,
} from '@mui/material';
import { httpClient } from '../services/http-client';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';

interface FeeRow {
  account: string;
  account_id: string;
  institution: string;
  owner: string;
  currency: string;
  trading_fees: string;
  account_fees: string;
  total: string;
  trade_count: number;
}

const WINDOWS = [
  { months: 12, label: '12 months' },
  { months: 24, label: '2 years' },
  { months: 60, label: '5 years' },
];

const num = (s?: string) => (s ? parseFloat(s) : 0);

/**
 * What the brokers charged.
 *
 * Commissions and account fees are kept APART throughout, because they are
 * different problems. A commission is the cost of a decision you made and
 * falls if you trade less. An account or management fee is the cost of
 * holding the account at all, and only changes if you move the money. Summing
 * them into one figure hides which of the two is worth acting on.
 *
 * Totals are per currency and never summed across — adding US and Canadian
 * fees at face value would misstate the bill by the exchange rate.
 */
export const FeesPage: React.FC = () => {
  const baseCurrency = useBaseCurrency();
  const [months, setMonths] = useState(12);
  const [rows, setRows] = useState<FeeRow[]>([]);
  const [totals, setTotals] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    httpClient
      .get<any>(`/investments/fees?months=${months}`)
      .then((r) => {
        if (cancelled) return;
        const d = (r as any)?.data ?? r;
        setRows(d?.accounts ?? []);
        setTotals(d?.totals ?? {});
        setError(null);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message ?? 'Could not load fees'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [months]);

  /** Which institution costs the most, so the answer is not buried in rows. */
  const byInstitution = useMemo(() => {
    const m = new Map<string, { total: number; currency: string }>();
    rows.forEach((r) => {
      const k = `${r.institution || '—'}·${r.currency}`;
      const cur = m.get(k) ?? { total: 0, currency: r.currency };
      cur.total += num(r.total);
      m.set(k, cur);
    });
    return Array.from(m.entries())
      .map(([k, v]) => ({ name: k.split('·')[0], ...v }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Fees</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        What your brokers charged. Trading commissions and account fees are
        shown separately &mdash; one falls if you trade less, the other only if
        you move the money.
      </Typography>

      <ToggleButtonGroup
        size="small" exclusive value={months}
        onChange={(_, v) => v && setMonths(v)}
        sx={{ mb: 3 }}
      >
        {WINDOWS.map((w) => (
          <ToggleButton key={w.months} value={w.months}>{w.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        {Object.entries(totals).map(([ccy, amount]) => (
          <Paper key={ccy} sx={{ p: 2, minWidth: 200, flex: '1 1 200px' }}>
            <Typography variant="overline" color="text.secondary">
              Paid in {ccy}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(amount, ccy as any, { base: baseCurrency as any })}
            </Typography>
          </Paper>
        ))}
      </Stack>

      {rows.length === 0 ? (
        <Alert severity="info">
          No fees recorded in this period. Commissions are captured from
          brokerage imports; an account with none may simply not charge them.
        </Alert>
      ) : (
        <>
          <Typography variant="overline" color="text.secondary">By institution</Typography>
          <Stack spacing={0.75} sx={{ mt: 1, mb: 3 }}>
            {byInstitution.map((i, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 200 }}>{i.name}</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="body2" fontWeight={650}>
                  {formatCurrency(i.total, i.currency as any, { base: baseCurrency as any })}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Commissions on buys and sells. Falls if you trade less.">
                      <span>Trading</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Management, administration and withholding. Charged for holding the account at all.">
                      <span>Account</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.account_id}-${r.currency}-${i}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={650}>{r.account}</Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {r.institution}
                        </Typography>
                        {r.currency !== baseCurrency && (
                          <Chip label={r.currency} size="small" variant="outlined" sx={{ height: 16 }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{r.owner}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {num(r.trading_fees) > 0 ? (
                        <>
                          {formatCurrency(r.trading_fees, r.currency as any, { base: baseCurrency as any })}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {r.trade_count} trades
                          </Typography>
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {num(r.account_fees) > 0
                        ? formatCurrency(r.account_fees, r.currency as any, { base: baseCurrency as any })
                        : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 650 }}>
                      {formatCurrency(r.total, r.currency as any, { base: baseCurrency as any })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
